import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LoadingState from "../components/common/LoadingState";
import {
  fetchCoachCommunicationSummary,
  fetchCoachCommunications,
  fetchNudgeAudit,
  fetchNudgeAuditUsers,
  fetchNudgeRuns,
  runNudgeAudit,
} from "../api/adminNudges";

const tone = {
  ok: "#057a55",
  blocked: "#b42318",
  muted: "#667085",
  border: "#d0d5dd",
  bg: "#f8fafc",
};

function valueText(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function StatusChip({ candidate }) {
  if (candidate?.would_send) return <Chip size="small" color="success" label="Winner" />;
  if (candidate?.eligible) return <Chip size="small" color="primary" label="Eligible" />;
  return <Chip size="small" color="default" label="Blocked" />;
}

function DetailList({ title, items }) {
  const rows = Array.isArray(items) ? items : [];
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      {rows.length ? (
        <Stack component="ul" sx={{ m: 0, pl: 2 }}>
          {rows.map((item, index) => (
            <Typography component="li" variant="body2" key={`${title}-${index}`}>
              {item}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">None</Typography>
      )}
    </Box>
  );
}

function CompanionSummary({ companion, moment }) {
  if (!companion && !moment) return <Typography variant="body2" color="text.secondary">No companion metadata</Typography>;
  return (
    <Stack spacing={0.5}>
      {companion ? (
        <>
          <Typography variant="body2">State: {valueText(companion.relationship_state)}</Typography>
          <Typography variant="body2">Mode: {valueText(companion.support_mode)}</Typography>
          <Typography variant="body2">Presence: {valueText(companion.presence_line)}</Typography>
          <Typography variant="body2">Goal: {valueText(companion.identity_anchor?.goal_title)}</Typography>
        </>
      ) : null}
      {moment ? (
        <>
          <Typography variant="body2">Moment: {valueText(moment.key)} / {valueText(moment.category)}</Typography>
          <Typography variant="body2">Why now: {valueText(moment.why_now)}</Typography>
          <Typography variant="body2">Target: {valueText(moment.target_action)}</Typography>
          <Typography variant="body2">Watching: {valueText(moment.watching)}</Typography>
        </>
      ) : null}
    </Stack>
  );
}

function CandidateDetail({ candidate }) {
  const metadata = candidate.opportunity?.metadata || candidate.payload || {};
  const semantics = metadata.commitment_semantics || {};
  const supportProfile = metadata.user_support_profile || {};
  const smartNudge = candidate.smart_nudge || {};
  const companion = candidate.companion || metadata.companion || null;
  const companionMoment = candidate.companion_moment || metadata.companion_moment || null;
  return (
    <Box sx={{ p: 2, bgcolor: "#f9fafb", borderTop: `1px solid ${tone.border}` }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Smart Nudge</Typography>
          <Typography variant="body2">Family: {valueText(smartNudge.family_label || candidate.smart_nudge_family)}</Typography>
          <Typography variant="body2">Access: {valueText(candidate.plus_or_free || smartNudge.plus_or_free)}</Typography>
          <Typography variant="body2">Plus enhanced: {valueText(candidate.plus_enhanced || smartNudge.plus_enhanced)}</Typography>
          <Typography variant="body2">Volume: {valueText(smartNudge.volume_policy)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Target Action</Typography>
          <Typography variant="body2">Action: {valueText(smartNudge.target_action || candidate.target)}</Typography>
          <Typography variant="body2">Deep link: {valueText(smartNudge.deeplink || candidate.deep_link)}</Typography>
          <Typography variant="body2">Delivery: {valueText(candidate.delivery?.channel || candidate.final_delivery_channel)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Exact Copy</Typography>
          <Typography variant="body2" fontWeight={700}>{valueText(smartNudge.exact_copy?.title || candidate.title)}</Typography>
          <Typography variant="body2">{valueText(smartNudge.exact_copy?.body || candidate.body)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">AI TrustBud Companion</Typography>
          <CompanionSummary companion={companion} moment={companionMoment} />
        </Grid>
        <Grid item xs={12} md={4}>
          <DetailList title="Qualified reasons" items={candidate.qualified_reasons} />
        </Grid>
        <Grid item xs={12} md={4}>
          <DetailList title="Blocked reasons" items={candidate.blocked_reasons} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Cooldown</Typography>
          <Typography variant="body2">Key: {valueText(candidate.cooldown?.cooldown_key)}</Typography>
          <Typography variant="body2">Blocked: {valueText(candidate.cooldown?.blocked)}</Typography>
          <Typography variant="body2">Last sent: {valueText(candidate.cooldown?.last_sent_at)}</Typography>
          <Typography variant="body2">Same-problem group: {valueText(candidate.semantic_dedupe?.overlap_group?.label)}</Typography>
          <Typography variant="body2">Same-problem log: {valueText(candidate.semantic_dedupe?.existing_log?.nudge_key)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">NudgeLog</Typography>
          <Typography variant="body2">Already sent today: {valueText(candidate.nudge_log?.already_sent_today)}</Typography>
          <Typography variant="body2">Matching logs: {candidate.nudge_log?.matching_logs?.length || 0}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Related</Typography>
          <Typography variant="body2">User: {valueText(candidate.related_user?.display_name)}</Typography>
          <Typography variant="body2">Connection: {valueText(candidate.related_connection_id)}</Typography>
          <Typography variant="body2">Accountability ID: {valueText(candidate.related_sprint_id)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Priority</Typography>
          <Typography variant="body2">{valueText(candidate.priority_explanation)}</Typography>
          <Typography variant="body2">Deep link: {valueText(candidate.deep_link)}</Typography>
          <Typography variant="body2">Final route: {valueText(candidate.final_route || candidate.deep_link)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Opportunity</Typography>
          <Typography variant="body2">Intent: {valueText(candidate.opportunity?.intent || candidate.intent)}</Typography>
          <Typography variant="body2">Habit: {valueText(candidate.opportunity?.recommended_user_action || candidate.recommended_user_action)}</Typography>
          <Typography variant="body2">Urgency: {valueText(candidate.opportunity?.urgency)}</Typography>
          <Typography variant="body2">Sensitivity: {valueText(candidate.opportunity?.sensitivity)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Delivery</Typography>
          <Typography variant="body2">Selected: {valueText(candidate.delivery?.channel || candidate.final_delivery_channel)}</Typography>
          <Typography variant="body2">Reason: {valueText(candidate.delivery?.reason)}</Typography>
          <Typography variant="body2">Allowed: {(candidate.opportunity?.allowed_channels || candidate.allowed_channels || []).join(", ") || "-"}</Typography>
          <Typography variant="body2">Expires: {valueText(candidate.opportunity?.expires_at)}</Typography>
          <Typography variant="body2">Outcome window: {valueText(candidate.opportunity?.outcome_window)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Coach Communication</Typography>
          <Typography variant="body2">Type: {valueText(candidate.communication_type)}</Typography>
          <Typography variant="body2">Purpose: {valueText(candidate.purpose)}</Typography>
          <Typography variant="body2">Category ID: {valueText(candidate.category_id)}</Typography>
          <Typography variant="body2">Notification action: {valueText(candidate.notification_action)}</Typography>
          <Typography variant="body2">Scoped key: {valueText(candidate.scoped_nudge_key || candidate.payload?.nudge_key)}</Typography>
          <Typography variant="body2">Channels: {(candidate.channels || []).join(", ") || "-"}</Typography>
          <Typography variant="body2">Preferred: {valueText(candidate.preferred_channel)}</Typography>
          <Typography variant="body2">Event-driven: {valueText(candidate.event_driven)}</Typography>
          <Typography variant="body2">Scheduled: {valueText(candidate.scheduled)}</Typography>
          <Typography variant="body2">Budget: {valueText(candidate.communication_budget?.used)}/{valueText(candidate.communication_budget?.limit)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Copy</Typography>
          <Typography variant="body2">Template: {valueText(candidate.copy_template_id)}</Typography>
          <Typography variant="body2">Source: {valueText(candidate.copy_source)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Commitment Semantics</Typography>
          <Typography variant="body2">Type: {valueText(semantics.commitment_type)}</Typography>
          <Typography variant="body2">Domain: {valueText(semantics.domain)}</Typography>
          <Typography variant="body2">Direction: {valueText(semantics.direction)}</Typography>
          <Typography variant="body2">Sensitive: {valueText(semantics.sensitive_domain)}</Typography>
          <Typography variant="body2">Source: {valueText(semantics.source)}</Typography>
          <Typography variant="body2">Safe fixes: {(semantics.safe_fix_families || []).join(", ") || "-"}</Typography>
          <Typography variant="body2">Blocked fixes: {(semantics.blocked_fix_families || []).join(", ") || "-"}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Support Profile</Typography>
          <Typography variant="body2">Style: {valueText(supportProfile.support_style)}</Typography>
          <Typography variant="body2">Intensity: {valueText(supportProfile.nudge_intensity)}</Typography>
          <Typography variant="body2">Preferred fixes: {(supportProfile.preferred_fix_families || []).join(", ") || "-"}</Typography>
          <Typography variant="body2">Blocked fixes: {(supportProfile.blocked_fix_families || []).join(", ") || "-"}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="overline" color="text.secondary">Personalization / Privacy</Typography>
          <Typography variant="body2">Fix family: {valueText(metadata.recommended_fix_family)}</Typography>
          <Typography variant="body2">Fatigue score: {valueText(metadata.fatigue_score)}</Typography>
          <Typography variant="body2">Intensity mode: {valueText(metadata.intensity_mode)}</Typography>
          <Typography variant="body2">Privacy: {valueText(metadata.privacy_level)}</Typography>
          <Typography variant="body2">Urgency: {valueText(metadata.urgency)}</Typography>
          <Typography variant="body2">Actionability: {valueText(metadata.actionability)}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="overline" color="text.secondary">Payload</Typography>
          <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: "#fff", border: `1px solid ${tone.border}`, overflow: "auto", fontSize: 12 }}>
            {JSON.stringify(candidate.payload || {}, null, 2)}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="overline" color="text.secondary">Blocked Channels</Typography>
          <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: "#fff", border: `1px solid ${tone.border}`, overflow: "auto", fontSize: 12 }}>
            {JSON.stringify(candidate.delivery?.blocked_channels || [], null, 2)}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function CandidateTable({ candidates }) {
  const [openKey, setOpenKey] = useState("");
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>Candidate Table</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>Family</TableCell>
              <TableCell>Plus/free</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type / purpose</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Overlap</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell>Related user</TableCell>
              <TableCell>Title / body</TableCell>
              <TableCell>Would send</TableCell>
              <TableCell>Blocked reasons</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(candidates || []).map((candidate) => {
              const rowKey = `${candidate.key}-${candidate.related_user?.id || ""}-${candidate.priority}`;
              const open = openKey === rowKey;
              return (
                <React.Fragment key={rowKey}>
                  <TableRow hover onClick={() => setOpenKey(open ? "" : rowKey)} sx={{ cursor: "pointer" }}>
                    <TableCell><StatusChip candidate={candidate} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ExpandMoreIcon fontSize="small" />
                        <Typography variant="body2">{candidate.key}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{valueText(candidate.smart_nudge?.family_label || candidate.smart_nudge_family)}</TableCell>
                    <TableCell>{valueText(candidate.plus_or_free)}</TableCell>
                    <TableCell>{candidate.category}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{valueText(candidate.communication_type)}</Typography>
                      <Typography variant="caption" color="text.secondary">{valueText(candidate.purpose)}</Typography>
                      {candidate.category_id ? (
                        <Typography variant="caption" display="block" color="text.secondary">{candidate.category_id}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{candidate.priority}</TableCell>
                    <TableCell>{candidate.source}</TableCell>
                    <TableCell>{valueText(candidate.overlap_group?.label)}</TableCell>
                    <TableCell>{valueText(candidate.delivery?.channel || candidate.final_delivery_channel)}</TableCell>
                    <TableCell>{valueText(candidate.related_user?.display_name)}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" fontWeight={700}>{valueText(candidate.title)}</Typography>
                      <Typography variant="caption" color="text.secondary">{valueText(candidate.body)}</Typography>
                    </TableCell>
                    <TableCell>{valueText(candidate.would_send)}</TableCell>
                    <TableCell sx={{ color: candidate.blocked_reasons?.length ? tone.blocked : tone.muted }}>
                      {(candidate.blocked_reasons || []).join("; ") || "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={14} sx={{ p: 0, borderBottom: open ? undefined : 0 }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <CandidateDetail candidate={candidate} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function AdminNudgeAuditPage() {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [period, setPeriod] = useState("auto");
  const [now, setNow] = useState("");
  const [audit, setAudit] = useState(null);
  const [runs, setRuns] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [communicationSummary, setCommunicationSummary] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingCommunications, setLoadingCommunications] = useState(false);
  const [loadingCommunicationSummary, setLoadingCommunicationSummary] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoadingUsers(true);
    fetchNudgeAuditUsers({ search, active_only: "1" })
      .then((data) => {
        if (!mounted) return;
        setUsers(data || []);
        if (data?.length) {
          setSelectedUser((current) => current || data[0]);
        }
      })
      .catch((err) => mounted && setError(err.message || "Unable to load users."))
      .finally(() => mounted && setLoadingUsers(false));
    return () => {
      mounted = false;
    };
  }, [search]);

  const loadRuns = () => {
    setLoadingRuns(true);
    fetchNudgeRuns()
      .then((data) => setRuns(data || []))
      .catch((err) => setError(err.message || "Unable to load nudge runs."))
      .finally(() => setLoadingRuns(false));
  };

  const loadCommunications = () => {
    setLoadingCommunications(true);
    fetchCoachCommunications({ limit: 40 })
      .then((data) => setCommunications(data || []))
      .catch((err) => setError(err.message || "Unable to load coach communications."))
      .finally(() => setLoadingCommunications(false));
  };

  const loadCommunicationSummary = () => {
    setLoadingCommunicationSummary(true);
    fetchCoachCommunicationSummary({ days: 30 })
      .then((data) => setCommunicationSummary(data || null))
      .catch((err) => setError(err.message || "Unable to load communication outcomes."))
      .finally(() => setLoadingCommunicationSummary(false));
  };

  useEffect(() => {
    loadRuns();
    loadCommunications();
    loadCommunicationSummary();
  }, []);

  const winner = audit?.winning_candidate;
  const primaryBlock = useMemo(() => {
    if (winner) return "";
    return audit?.global_eligibility?.reason || audit?.blocked_candidates?.[0]?.blocked_reasons?.[0] || "No candidate qualified";
  }, [audit, winner]);

  const runAudit = () => {
    if (!selectedUser) return;
    setRunning(true);
    setError("");
    fetchNudgeAudit({ user_id: selectedUser.id, period, now })
      .then(setAudit)
      .catch((err) => setError(err.message || "Unable to run audit."))
      .finally(() => setRunning(false));
  };

  const runRealSend = () => {
    if (!selectedUser) return;
    if (!window.confirm("Send a real push for this user now?")) return;
    setRunning(true);
    setError("");
    runNudgeAudit({ user_id: selectedUser.id, period, now, real_send: true })
      .then((data) => {
        setSelectedRun(data);
        loadRuns();
      })
      .catch((err) => setError(err.message || "Unable to send nudge."))
      .finally(() => setRunning(false));
  };

  const openRun = (runId) => {
    setLoadingRuns(true);
    fetchNudgeRuns({ run_id: runId })
      .then(setSelectedRun)
      .catch((err) => setError(err.message || "Unable to load nudge run."))
      .finally(() => setLoadingRuns(false));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: tone.bg, minHeight: "100%" }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Coach Communication Audit</Typography>
          <Typography variant="body2" color="text.secondary">
            Admin-only view of scheduled pushes, in-app coach moments, delivery decisions, and exact copy.
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="h6">User Search</Typography>
                  <TextField
                    size="small"
                    label="Search name or email"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
                  />
                  {loadingUsers ? <LoadingState title="Loading users" /> : (
                    <Stack spacing={1} sx={{ maxHeight: 300, overflow: "auto" }}>
                      {users.map((user) => (
                        <Button
                          key={user.id}
                          variant={selectedUser?.id === user.id ? "contained" : "outlined"}
                          onClick={() => setSelectedUser(user)}
                          sx={{ justifyContent: "space-between", textAlign: "left" }}
                        >
                          <span>{user.display_name} · {user.email}</span>
                          <span>{user.has_active_push_device ? "Push" : "No push"}{user.active_sprint_partner ? ` · ${user.active_sprint_partner}` : ""}</span>
                        </Button>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Run Controls</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Period</InputLabel>
                        <Select value={period} label="Period" onChange={(event) => setPeriod(event.target.value)}>
                          <MenuItem value="auto">auto</MenuItem>
                          <MenuItem value="morning">morning</MenuItem>
                          <MenuItem value="midday">midday</MenuItem>
                          <MenuItem value="evening">evening</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        size="small"
                        type="datetime-local"
                        label="Simulated now"
                        InputLabelProps={{ shrink: true }}
                        value={now}
                        onChange={(event) => setNow(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button fullWidth variant="contained" startIcon={<PlayArrowIcon />} onClick={runAudit} disabled={!selectedUser || running}>
                        Dry run
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button color="warning" variant="outlined" onClick={runRealSend} disabled={!selectedUser || running}>
                        Real send now
                      </Button>
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="text.secondary">
                    Selected: {selectedUser ? `${selectedUser.display_name} (${selectedUser.email})` : "None"}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {running && <LoadingState title="Running audit" />}

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6">Scheduled Communication Runs</Typography>
                <Typography variant="body2" color="text.secondary">Recent scheduled/manual push runs and delivery outcomes.</Typography>
              </Box>
              <Button onClick={loadRuns} disabled={loadingRuns}>Refresh</Button>
            </Stack>
            {loadingRuns ? <LoadingState title="Loading runs" /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Skipped</TableCell>
                    <TableCell>Failed</TableCell>
                    <TableCell>Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(runs || []).map((run) => (
                    <TableRow hover key={run.id} onClick={() => openRun(run.id)} sx={{ cursor: "pointer" }}>
                      <TableCell>{run.id}</TableCell>
                      <TableCell>{run.job_source}</TableCell>
                      <TableCell>{run.period}</TableCell>
                      <TableCell>{run.status}</TableCell>
                      <TableCell>{valueText(run.started_at)}</TableCell>
                      <TableCell>{run.total_users_considered}</TableCell>
                      <TableCell>{run.users_sent}</TableCell>
                      <TableCell>{run.users_skipped}</TableCell>
                      <TableCell>{run.users_failed}</TableCell>
                      <TableCell>{valueText(run.duration_ms)}ms</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6">Communication Outcomes</Typography>
                <Typography variant="body2" color="text.secondary">
                  Last {communicationSummary?.window_days || 30} days by type and purpose.
                </Typography>
              </Box>
              <Button onClick={loadCommunicationSummary} disabled={loadingCommunicationSummary}>Refresh</Button>
            </Stack>
            {loadingCommunicationSummary ? <LoadingState title="Loading outcomes" /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Push</TableCell>
                    <TableCell>In-app</TableCell>
                    <TableCell>Acted</TableCell>
                    <TableCell>Action rate</TableCell>
                    <TableCell>Target habit</TableCell>
                    <TableCell>Keep-alive step</TableCell>
                    <TableCell>Avg time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(communicationSummary?.rows || []).map((row) => (
                    <TableRow key={`${row.communication_type}-${row.purpose}`}>
                      <TableCell>{valueText(row.communication_type)}</TableCell>
                      <TableCell>{valueText(row.purpose)}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.push}</TableCell>
                      <TableCell>{row.in_app}</TableCell>
                      <TableCell>{row.acted}</TableCell>
                      <TableCell>{Math.round((row.action_rate || 0) * 100)}%</TableCell>
                      <TableCell>{row.target_habit_acted}</TableCell>
                      <TableCell>{row.small_version_used}</TableCell>
                      <TableCell>{row.avg_hours_to_action === null || row.avg_hours_to_action === undefined ? "-" : `${row.avg_hours_to_action}h`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6">Recent Coach Communications</Typography>
                <Typography variant="body2" color="text.secondary">
                  Exact push and in-app copy, including completion celebrations that do not call Expo.
                </Typography>
              </Box>
              <Button onClick={loadCommunications} disabled={loadingCommunications}>Refresh</Button>
            </Stack>
            {loadingCommunications ? <LoadingState title="Loading communications" /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Companion</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell>Copy</TableCell>
                    <TableCell>Deeplink</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(communications || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{valueText(item.sent_at)}</TableCell>
                      <TableCell>{item.user?.username || item.user?.email || item.user?.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{valueText(item.decision || "sent")}</Typography>
                        {item.blocked_reason ? (
                          <Typography variant="caption" color="error">{item.blocked_reason}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{valueText(item.actual_channel || item.channel)}</Typography>
                        <Typography variant="caption" color="text.secondary">{(item.channels || []).join(", ")}</Typography>
                      </TableCell>
                      <TableCell>{valueText(item.communication_type)}</TableCell>
                      <TableCell>{valueText(item.purpose)}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <CompanionSummary companion={item.companion} moment={item.companion_moment} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.outcome_counts?.acted ? `${item.outcome_counts.acted} acted` : "No action yet"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {valueText(item.latest_outcome?.metadata?.hours_since_communication)}
                          {item.latest_outcome?.metadata?.hours_since_communication !== undefined ? "h later" : ""}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" fontWeight={700}>{valueText(item.title)}</Typography>
                        <Typography variant="caption" color="text.secondary">{valueText(item.body)}</Typography>
                      </TableCell>
                      <TableCell>{valueText(item.deeplink)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {selectedRun && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">Run #{selectedRun.id} Decision Trail</Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell>Window</TableCell>
                    <TableCell>Winner</TableCell>
                    <TableCell>Type / purpose</TableCell>
                    <TableCell>Expo</TableCell>
                    <TableCell>Communication Log</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Payload</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedRun.users || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.user?.username || item.user?.email || item.user?.id}</TableCell>
                      <TableCell><Chip size="small" label={item.final_decision} /></TableCell>
                      <TableCell>{item.behavior_window}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{valueText(item.winner_key)}</Typography>
                        <Typography variant="caption" color="text.secondary">{valueText(item.winner_category)} · {valueText(item.winner_priority)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{valueText(item.communication_type)}</Typography>
                        <Typography variant="caption" color="text.secondary">{valueText(item.purpose)}</Typography>
                        <Typography variant="caption" display="block">{(item.channels || []).join(", ")}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">Called: {valueText(item.expo_called)}</Typography>
                        <Typography variant="body2">Accepted: {valueText(item.expo_accepted)}</Typography>
                      </TableCell>
                      <TableCell>{item.nudge_log_created ? `Created #${item.nudge_log_id}` : "No"}</TableCell>
                      <TableCell>{valueText(item.blocked_reason)}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" fontWeight={700}>{valueText(item.title)}</Typography>
                        <Typography variant="caption" color="text.secondary">{valueText(item.body)}</Typography>
                        <Typography variant="caption" display="block">Deep link: {valueText(item.deeplink)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box component="pre" sx={{ mt: 1.5, p: 1.5, bgcolor: "#fff", border: `1px solid ${tone.border}`, overflow: "auto", fontSize: 12 }}>
                {JSON.stringify(selectedRun, null, 2)}
              </Box>
            </CardContent>
          </Card>
        )}

        {audit && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">Global Eligibility</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>Active push device</Grid><Grid item xs={6}>{valueText(audit.global_eligibility?.has_active_push_device)}</Grid>
                      <Grid item xs={6}>Quiet hours</Grid><Grid item xs={6}>{valueText(audit.global_eligibility?.is_in_quiet_hours)}</Grid>
                      <Grid item xs={6}>Local time</Grid><Grid item xs={6}>{valueText(audit.run_context?.local_now)}</Grid>
                      <Grid item xs={6}>Local date</Grid><Grid item xs={6}>{valueText(audit.run_context?.local_date)}</Grid>
                      <Grid item xs={6}>Period</Grid><Grid item xs={6}>{valueText(audit.run_context?.period)}</Grid>
                      <Grid item xs={6}>Behavioral eligible</Grid><Grid item xs={6}>{valueText(audit.global_eligibility?.eligible_for_behavioral_push)}</Grid>
                    </Grid>
                    <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                      Communication Preferences
                    </Typography>
                    <Grid container spacing={1}>
                      {Object.entries(audit.communication_preferences || {}).map(([key, value]) => (
                        <React.Fragment key={key}>
                          <Grid item xs={7}>{key}</Grid>
                          <Grid item xs={5}>{valueText(value)}</Grid>
                        </React.Fragment>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={7}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">Winning Candidate</Typography>
                    <Divider sx={{ my: 1 }} />
                    {winner ? (
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip label={winner.key} />
                          <Chip label={winner.category} />
                          <Chip label={winner.communication_type || "communication"} />
                          <Chip label={winner.purpose || "purpose"} />
                          <Chip label={`Priority ${winner.priority}`} />
                          <Chip label={winner.target || "no target"} />
                        </Stack>
                        <Typography variant="subtitle1" fontWeight={800}>{winner.title}</Typography>
                        <Typography variant="body2">{winner.body}</Typography>
                        <Typography variant="body2" color="text.secondary">Deep link: {winner.deep_link}</Typography>
                        <DetailList title="Why it won" items={winner.qualified_reasons} />
                      </Stack>
                    ) : (
                      <Alert severity="info">No push would be sent. {primaryBlock}</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Canonical Behavior State</Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={6} md={3}>State</Grid><Grid item xs={6} md={3}>{valueText(audit.canonical_consistency_state?.state)}</Grid>
                  <Grid item xs={6} md={3}>Severity</Grid><Grid item xs={6} md={3}>{valueText(audit.canonical_consistency_state?.severity)}</Grid>
                  <Grid item xs={6} md={3}>Acted today</Grid><Grid item xs={6} md={3}>{valueText(audit.canonical_consistency_state?.has_acted_today)}</Grid>
                  <Grid item xs={6} md={3}>Missed yesterday</Grid><Grid item xs={6} md={3}>{valueText(audit.canonical_consistency_state?.missed_yesterday)}</Grid>
                  <Grid item xs={6} md={3}>Miss streak</Grid><Grid item xs={6} md={3}>{valueText(audit.canonical_consistency_state?.miss_streak)}</Grid>
                  <Grid item xs={6} md={3}>Recovered today</Grid><Grid item xs={6} md={3}>{valueText(audit.canonical_consistency_state?.recovered_today)}</Grid>
                  <Grid item xs={12}>Reason: {valueText(audit.canonical_consistency_state?.reason)}</Grid>
                </Grid>
                <Box component="pre" sx={{ mt: 1.5, p: 1.5, bgcolor: "#fff", border: `1px solid ${tone.border}`, overflow: "auto", fontSize: 12 }}>
                  {JSON.stringify(audit.canonical_consistency_state?.meaningful_activity_events || [], null, 2)}
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Priority Hierarchy</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  {(audit.priority_hierarchy || []).map((item) => (
                    <Chip
                      key={item.category}
                      label={`${item.rank}. ${item.category} (${item.base_priority})`}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <CandidateTable candidates={audit.candidates} />

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Blocked Candidates</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {(audit.blocked_candidates || []).map((candidate) => (
                    <Box key={`${candidate.key}-${candidate.priority}`} sx={{ p: 1.25, border: `1px solid ${tone.border}`, borderRadius: 1, bgcolor: "#fff" }}>
                      <Typography variant="body2" fontWeight={800}>{candidate.key}</Typography>
                      <Typography variant="caption" color="text.secondary">{(candidate.blocked_reasons || []).join("; ") || "No reason recorded"}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </Box>
  );
}
